import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OfflineProvider } from "./context/OfflineContext";
import { SuperAdminAuthProvider } from "./context/SuperAdminAuthContext";
import { AppLayout } from "./components/layout/AppLayout";
import { Spinner } from "./components/ui/Spinner";
import { esDominioRaiz } from "./lib/subdomain";
import { ShieldOff } from "lucide-react";

import Landing from "./pages/Landing";
import AltaCompletado from "./pages/AltaCompletado";
import Tutoriales from "./pages/Tutoriales";
import AvisoPrivacidad from "./pages/AvisoPrivacidad";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clientes from "./pages/Clientes";
import Mascotas from "./pages/Mascotas";
import MascotaDetalle from "./pages/MascotaDetalle";
import Agenda from "./pages/Agenda";
import PuntoDeVenta from "./pages/PuntoDeVenta";
import Inventario from "./pages/Inventario";
import Facturacion from "./pages/Facturacion";
import Recordatorios from "./pages/Recordatorios";
import Hospedaje from "./pages/Hospedaje";
import Espacios from "./pages/Espacios";
import Grooming from "./pages/Grooming";
import Entrenamiento from "./pages/Entrenamiento";
import Domicilio from "./pages/Domicilio";
import Paseos from "./pages/Paseos";
import Reportes from "./pages/Reportes";
import Usuarios from "./pages/Usuarios";
import Configuracion from "./pages/Configuracion";
import Suscripcion from "./pages/Suscripcion";
import Transferencias from "./pages/Transferencias";
import SoporteTecnico from "./pages/SoporteTecnico";
import ExpedientePublico from "./pages/ExpedientePublico";
import CertificadoVacuna from "./pages/CertificadoVacuna";
import CertificadoSalud from "./pages/CertificadoSalud";
import RespuestasClientes from "./pages/RespuestasClientes";

import SuperAdminLogin from "./pages/SuperAdmin/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdmin/SuperAdminDashboard";
import Campanas from "./pages/SuperAdmin/Campanas";
import WhatsappInbox from "./pages/SuperAdmin/WhatsappInbox";
import Analitica from "./pages/SuperAdmin/Analitica";

function RutaPrivada({ children }: { children: JSX.Element }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  if (!usuario) return <Navigate to="/login" replace />;
  return children;
}

// Bloquea la navegación directa por URL a una sección que el negocio marcó
// como OCULTA para el rol del usuario actual (el sidebar ya no muestra el
// enlace, pero esto cubre a quien escriba la ruta a mano). No se usa
// Navigate para evitar loops si justo "dashboard" quedara oculto: se
// muestra un aviso simple en su lugar.
function RutaSeccion({ seccion, children }: { seccion: string; children: JSX.Element }) {
  const { permisoSeccion } = useAuth();
  if (permisoSeccion(seccion) === "OCULTO") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-500">
        <ShieldOff size={40} className="text-slate-300" />
        <p className="font-medium">No tienes acceso a esta sección</p>
        <p className="text-sm text-slate-400">Pídele a un administrador que revise tus permisos.</p>
      </div>
    );
  }
  return children;
}

function RutasNegocio() {
  return (
    <AuthProvider>
      <OfflineProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Alta de negocios deshabilitada: ahora se da de alta solo desde el
            portal del super administrador. */}
        <Route path="/registro" element={<Navigate to="/login" replace />} />
        {/* Portal público del expediente de una mascota: sin login, acceso
            solo por el enlace con token que le llega al cliente por
            WhatsApp/correo. Va fuera de RutaPrivada a propósito. */}
        <Route path="/portal-mascota/:token" element={<ExpedientePublico />} />
        <Route path="/portal-mascota/:token/vacuna/:vacunaId/certificado" element={<CertificadoVacuna />} />
        <Route path="/portal-mascota/:token/certificado-salud/:certificadoId" element={<CertificadoSalud />} />
        <Route
          element={
            <RutaPrivada>
              <AppLayout />
            </RutaPrivada>
          }
        >
          <Route path="/" element={<RutaSeccion seccion="dashboard"><Dashboard /></RutaSeccion>} />
          <Route path="/clientes" element={<RutaSeccion seccion="clientes"><Clientes /></RutaSeccion>} />
          <Route path="/mascotas" element={<RutaSeccion seccion="mascotas"><Mascotas /></RutaSeccion>} />
          <Route path="/mascotas/:id" element={<RutaSeccion seccion="mascotas"><MascotaDetalle /></RutaSeccion>} />
          <Route path="/agenda" element={<RutaSeccion seccion="agenda"><Agenda /></RutaSeccion>} />
          <Route path="/punto-de-venta" element={<RutaSeccion seccion="punto-de-venta"><PuntoDeVenta /></RutaSeccion>} />
          <Route path="/inventario" element={<RutaSeccion seccion="inventario"><Inventario /></RutaSeccion>} />
          <Route path="/facturacion" element={<RutaSeccion seccion="facturacion"><Facturacion /></RutaSeccion>} />
          <Route path="/recordatorios" element={<RutaSeccion seccion="recordatorios"><Recordatorios /></RutaSeccion>} />
          <Route path="/hospedaje" element={<RutaSeccion seccion="hospedaje"><Hospedaje /></RutaSeccion>} />
          <Route path="/espacios" element={<RutaSeccion seccion="espacios"><Espacios /></RutaSeccion>} />
          <Route path="/grooming" element={<RutaSeccion seccion="grooming"><Grooming /></RutaSeccion>} />
          <Route path="/entrenamiento" element={<RutaSeccion seccion="entrenamiento"><Entrenamiento /></RutaSeccion>} />
          <Route path="/domicilio" element={<RutaSeccion seccion="domicilio"><Domicilio /></RutaSeccion>} />
          <Route path="/paseos" element={<RutaSeccion seccion="paseos"><Paseos /></RutaSeccion>} />
          <Route path="/reportes" element={<RutaSeccion seccion="reportes"><Reportes /></RutaSeccion>} />
          <Route path="/usuarios" element={<RutaSeccion seccion="usuarios"><Usuarios /></RutaSeccion>} />
          <Route path="/configuracion" element={<RutaSeccion seccion="configuracion"><Configuracion /></RutaSeccion>} />
          <Route path="/suscripcion" element={<RutaSeccion seccion="suscripcion"><Suscripcion /></RutaSeccion>} />
          <Route path="/transferencias" element={<RutaSeccion seccion="transferencias"><Transferencias /></RutaSeccion>} />
          <Route path="/soporte-tecnico" element={<RutaSeccion seccion="soporte-tecnico"><SoporteTecnico /></RutaSeccion>} />
          <Route path="/respuestas-clientes" element={<RutaSeccion seccion="respuestas-clientes"><RespuestasClientes /></RutaSeccion>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </OfflineProvider>
    </AuthProvider>
  );
}

function RutasSuperAdmin() {
  return (
    <SuperAdminAuthProvider>
      <Routes>
        <Route path="/login" element={<SuperAdminLogin />} />
        <Route path="/" element={<SuperAdminDashboard />} />
        <Route path="/campanas" element={<Campanas />} />
        <Route path="/whatsapp" element={<WhatsappInbox />} />
        <Route path="/analitica" element={<Analitica />} />
        <Route path="*" element={<Navigate to="/superadmin" replace />} />
      </Routes>
    </SuperAdminAuthProvider>
  );
}

export default function App() {
  // El dominio raíz (mascotas.vet / www.mascotas.vet) muestra el portal de
  // inicio público en vez del login de un negocio — el hostname no cambia
  // durante la sesión, así que basta con revisarlo una sola vez aquí.
  if (esDominioRaiz()) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/alta/completado" element={<AltaCompletado />} />
          <Route path="/tutoriales" element={<Tutoriales />} />
          <Route path="/aviso-de-privacidad" element={<AvisoPrivacidad />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Portal del super administrador: árbol de rutas y contexto de
            autenticación totalmente separados del de los negocios. */}
        <Route path="/superadmin/*" element={<RutasSuperAdmin />} />
        <Route path="/*" element={<RutasNegocio />} />
      </Routes>
    </BrowserRouter>
  );
}
